import { useState, useEffect, useMemo } from "react"
import { ShoppingCart } from "lucide-react"
import { DS } from "../lib/ui"
import {
  qtyAchat,
  qtyPrelevable,
  echelleAchatPrelevable,
  prixUnitePrelevable,
} from "../lib/depots-helpers"

import useEchapPourFermer from "../lib/useEchapPourFermer"
// Saisie d'un achat (entrée admin → réapprovisionnement stock central) en UNITÉ PRÉLEVABLE.
// Schéma de l'objet retourné via onSave :
//   { id, date "YYYY-MM-DD", csId, qtyAchetee, mode "A"|"P",
//     qtyAjoutPrelevable (qtyAchetee × scale), prixTotal,
//     prixUnitaire (par unité prélevable), enseigne?, notes?, createdAt }
//
// Toggle multi-unité (si qtyParAchat > qtyParPrelevable) :
//   * Paquet entier (mode "A") : qtyAjoutPrelevable = qtyAchetee × (qtyA / qtyP)
//   * Sous-paquet  (mode "P") : qtyAjoutPrelevable = qtyAchetee × 1
// Sinon (qtyA == qtyP) : pas de toggle, qtyAjoutPrelevable = qtyAchetee.
export default function SaisirAchatModal({
  produits, defaultCsId, onSave, onClose,
}) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  const today = new Date().toISOString().split("T")[0]
  const sortedProds = useMemo(
    () => [...produits].sort((a, b) => (a.nom || "").localeCompare(b.nom || "")),
    [produits]
  )

  const [date, setDate] = useState(today)
  const [csId, setCsId] = useState(defaultCsId || sortedProds[0]?.id || "")
  const [qtyAchetee, setQtyAchetee] = useState(1)
  const [mode, setMode] = useState("A")
  const [prixTotal, setPrixTotal] = useState("")
  const [enseigne, setEnseigne] = useState("")
  const [enseigneTouched, setEnseigneTouched] = useState(false)
  const [notes, setNotes] = useState("")
  const [saving, setSaving] = useState(false)

  const prod = sortedProds.find(p => p.id === csId)
  // ⚠️ L'ÉCHELLE VIENT DU HELPER PARTAGÉ (revue adverse #1449, MAJEUR 2).
  // Cette modale calculait `qtyParAchat ?? 1`, SANS le repli sur l'ancien
  // champ `contenance` que tous ses voisins honorent (le serveur, le prix
  // qu'elle affiche elle-même juste en dessous) : un produit pré-migration
  // `{contenance: 48, qtyParPrelevable: 6}` donnait 1 ici et 8 côté serveur.
  const qtyA = qtyAchat(prod)
  const qtyP = qtyPrelevable(prod)
  const multi = qtyA > qtyP
  // qtyAjoutPrelevable = nb d'unités prélevables ajoutées au stock central.
  const scalePrelevable = echelleAchatPrelevable(prod, mode)
  const qtyAjoutPrelevable = qtyAchetee * scalePrelevable
  const labelPrelevable = !multi ? (prod?.formatAchat || "paquet") : `sous-paquet de ${qtyP}`

  // Pré-remplit l'enseigne depuis le produit tant que l'utilisateur ne l'a pas modifiée.
  useEffect(() => {
    if (!enseigneTouched) setEnseigne(prod?.enseigne || "")
  }, [csId, prod, enseigneTouched])

  // Reset mode à "A" quand on change de produit (paquet entier = défaut achat usuel).
  useEffect(() => { setMode("A") }, [csId])

  const prixTotalNum = Number(prixTotal) || 0
  // Prix par unité prélevable (basé sur ce que l'admin a réellement payé).
  const prixUnitaire = qtyAjoutPrelevable > 0
    ? Math.round((prixTotalNum / qtyAjoutPrelevable) * 1000) / 1000
    : 0
  const prixRefPrelevable = prixUnitePrelevable(prod)
  const ecartRefPct = prixRefPrelevable > 0
    ? ((prixUnitaire - prixRefPrelevable) / prixRefPrelevable) * 100
    : 0

  const canSave = !!csId && qtyAchetee > 0 && prixTotalNum > 0 && !!date

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    const achat = {
      id: "ach_" + Math.random().toString(36).slice(2, 10),
      date,
      csId,
      qtyAchetee: Math.floor(qtyAchetee),
      mode: !multi ? "A" : mode,
      qtyAjoutPrelevable,
      prixTotal: Math.round(prixTotalNum * 100) / 100,
      prixUnitaire,
      enseigne: enseigne.trim() || undefined,
      notes: notes.trim() || undefined,
      createdAt: new Date().toISOString(),
    }
    try { await onSave(achat) } finally { setSaving(false) }
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:1001,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="fbz-modal-sheet" style={{background:DS.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",fontFamily:DS.fontBody}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${DS.border}`,position:"sticky",top:0,background:DS.card,zIndex:2}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:17}}><ShoppingCart size={17} strokeWidth={2.2}/>Saisir un achat</div>
              <div style={{fontSize:11,color:DS.textMuted,marginTop:2}}>Réapprovisionne le stock central</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:DS.textMuted}}>×</button>
          </div>
        </div>

        <div style={{padding:"16px 20px"}}>
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Produit</label>
            <select value={csId} onChange={e=>{ setCsId(e.target.value); setEnseigneTouched(false) }}
              style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy,fontWeight:600}}>
              {sortedProds.map(p => (<option key={p.id} value={p.id}>{p.nom}</option>))}
            </select>
            {prod && (
              <div style={{fontSize:10,color:DS.textMuted,marginTop:4}}>
                Paquet = {qtyA}{multi && <span> · Sous-paquet = {qtyP}</span>}
                {prod.prixAchat != null && <span> · prix réf. {prod.prixAchat.toFixed(2)} €</span>}
              </div>
            )}
          </div>

          {/* Toggle multi-unité */}
          {multi && (
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Unité d'achat</label>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>setMode("A")}
                  style={{flex:1,padding:"8px 10px",background:mode==="A"?DS.navy:DS.cielSoft,color:mode==="A"?"#fff":DS.navy,border:"none",borderRadius:8,fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:DS.fontBody}}>
                  Paquet entier (= {qtyA / qtyP} sous-paquets)
                </button>
                <button onClick={()=>setMode("P")}
                  style={{flex:1,padding:"8px 10px",background:mode==="P"?DS.navy:DS.cielSoft,color:mode==="P"?"#fff":DS.navy,border:"none",borderRadius:8,fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:DS.fontBody}}>
                  Sous-paquet (×{qtyP})
                </button>
              </div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Date</label>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)}
                style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy}}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>
                Quantité ({!multi ? "paquet" : (mode === "A" ? "paquet" : "sous-paquet")})
              </label>
              <input type="number" min="1" value={qtyAchetee}
                onChange={e=>setQtyAchetee(Math.max(1, parseInt(e.target.value)||1))}
                style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontWeight:700,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy}}/>
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Prix total payé (€)</label>
              <input type="number" step="0.01" min="0" value={prixTotal}
                placeholder={prixRefPrelevable > 0 ? (prixRefPrelevable * qtyAjoutPrelevable).toFixed(2) : ""}
                onChange={e=>setPrixTotal(e.target.value)}
                style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontWeight:700,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy}}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Prix par {labelPrelevable} (calculé)</label>
              <div style={{padding:"8px 10px",background:DS.cielSoft,borderRadius:8,fontSize:13,fontWeight:700,color:DS.navy}}>
                {prixTotalNum > 0 ? `${prixUnitaire.toFixed(3)} €` : "—"}
                {prixTotalNum > 0 && prixRefPrelevable > 0 && (
                  <span style={{fontSize:10,marginLeft:6,color: Math.abs(ecartRefPct) > 5 ? DS.warning : DS.textMuted,fontWeight:600}}>
                    ({ecartRefPct >= 0 ? "+" : ""}{ecartRefPct.toFixed(0)}% vs réf.)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Enseigne</label>
            <input type="text" value={enseigne}
              onChange={e=>{ setEnseigne(e.target.value); setEnseigneTouched(true) }}
              style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy}}/>
          </div>

          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Notes (optionnel)</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
              style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"8px 10px",fontSize:12,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.navy,resize:"vertical"}}/>
          </div>

          <div style={{background:DS.cielSoft,borderRadius:DS.radiusCard,padding:"12px 14px",marginTop:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:700,color:DS.marineDeep,textTransform:"uppercase",letterSpacing:.5}}>Stock à ajouter</span>
            <span style={{fontFamily:DS.fontDisplay,fontSize:18,fontWeight:700,color:DS.marine}}>
              +{qtyAjoutPrelevable} {labelPrelevable}{qtyAjoutPrelevable>1?"s":""}
            </span>
          </div>
        </div>

        <div style={{padding:"12px 20px",borderTop:`1px solid ${DS.border}`,position:"sticky",bottom:0,background:DS.card,display:"flex",gap:8}}>
          <button className="fbz-press" onClick={onClose} disabled={saving}
            style={{flex:1,padding:11,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:DS.fontBody}}>Annuler</button>
          <button className="fbz-press" onClick={handleSave} disabled={!canSave || saving}
            style={{flex:2,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,padding:11,background:saving||!canSave?DS.borderHover:DS.corail,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:700,fontSize:12,cursor:saving||!canSave?"not-allowed":"pointer",fontFamily:DS.fontBody}}>
            {saving ? "..." : <><ShoppingCart size={15} strokeWidth={2.2}/>Enregistrer l'achat</>}
          </button>
        </div>
      </div>
    </div>
  )
}
